import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Shield, User, Clock, Trash2, Cookie, Lock } from "lucide-react";
import TopNav from "../components/TopNav";
import AppShell from "../components/AppShell";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { api, useAuth } from "../context/AuthContext";
import { Loader2 } from "lucide-react";

export default function PrivacyPolicy() {
  const { user, loading, login } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [msg, setMsg] = useState(null);

  const requestDeletion = async () => {
    if (!user) return login();
    if (!window.confirm("Delete your Gumble account and all associated data? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.delete("/user/me");
      setMsg({ type: "ok", text: "Account deleted. You will be signed out." });
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed." });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/60"><Loader2 className="animate-spin" /></div>;

  const body = (
    <>
      <SEO
        title="Privacy Policy"
        description="How Gumble Studio collects, uses, and deletes your data. Discord login, Stake links, cookies, retention and your rights."
        path="/privacy-policy"
      />
      <div className={`${user ? "pt-10 md:pt-14" : "pt-28"} px-6 md:px-10 max-w-3xl mx-auto pb-20`}>
        <h1 className="text-4xl md:text-5xl font-black tracking-[-0.03em]">Privacy Policy</h1>
        <p className="mt-3 text-white/60 text-[13px]">Last updated: February 12, 2026 · Gumble Studio</p>

        {/* Quick cards */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card icon={Shield} title="18+" kicker="Adults only" text="You must be 18 or older to use Gumble." />
          <Card icon={Lock} title="No selling" kicker="Your data" text="We never sell personal data." />
          <Card icon={Clock} title="Fast delete" kicker="Your right" text="Delete your account anytime." />
        </div>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-white/75">
          <Section title="1. Who we are">
            <p>Gumble Studio operates <a href="https://gumble.games" className="text-white underline decoration-white/20 underline-offset-2">gumble.games</a>. Contact: <a href="mailto:privacy@gumble.games" className="text-white underline">privacy@gumble.games</a>.</p>
          </Section>

          <Section title="2. What we collect">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><b className="text-white">Discord login:</b> Discord ID, username, display name, avatar, email (as provided by Discord OAuth).</li>
              <li><b className="text-white">Stake link:</b> your Stake username and verification status. We do not receive your Stake password or wallet.</li>
              <li><b className="text-white">Activity:</b> submitted bet IDs, crate events, messages via the Partner form, and support messages.</li>
              <li><b className="text-white">Technical:</b> session tokens, request logs, and standard web metrics (no cross-site tracking).</li>
            </ul>
          </Section>

          <Section title="3. Cookies">
            <p className="flex items-start gap-2"><Cookie className="w-4 h-4 mt-1 text-white/60" />We use only essential cookies: a session cookie to keep you signed in. No advertising cookies.</p>
          </Section>

          <Section title="4. How we use it">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Run challenges, crates, leaderboards, and admin review queues.</li>
              <li>Verify Stake account ownership and bet eligibility.</li>
              <li>Respond to partner and support enquiries.</li>
            </ul>
          </Section>

          <Section title="5. Sharing">
            <p>We share data only with: <b className="text-white">Discord</b> (authentication), <b className="text-white">Stake</b> (bet verification), and our hosting/database providers (Supabase / Netlify) under contractual obligations. We do not sell data to third parties.</p>
          </Section>

          <Section title="6. Retention">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Sessions: 30 days (or until sign-out).</li>
              <li>Accounts and bets: until you delete your account.</li>
              <li>Partner messages: up to 24 months for business records.</li>
            </ul>
          </Section>

          <Section title="7. Your rights & deletion">
            <p>You can request access, correction, or deletion of your data at any time. The fastest path is the button below (requires sign-in), or email <a href="mailto:privacy@gumble.games" className="text-white underline">privacy@gumble.games</a>.</p>
            <div className="mt-4 glass rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-white flex items-center gap-2"><Trash2 className="w-4 h-4" /> Delete my account & data</div>
                <div className="mt-1 text-[13px] text-white/60">Removes your profile, sessions, and linked Stake handle. Bets/crates remain anonymized for audit.</div>
              </div>
              <button onClick={requestDeletion} disabled={deleting}
                className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-[13px] font-semibold hover:bg-white/90 disabled:opacity-50">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {user ? "Delete account" : "Sign in to delete"}
              </button>
            </div>
            {msg && <div className={`mt-3 text-[13px] ${msg.type === "ok" ? "text-emerald-300" : "text-red-300"}`}>{msg.text}</div>}
            <p className="mt-3 text-[13px] text-white/50">Prefer email? Write to <a href="mailto:privacy@gumble.games" className="text-white/80 underline">privacy@gumble.games</a> with subject "Data deletion".</p>
          </Section>

          <Section title="8. Minors">
            <p>The site is for adults 18+. If you are under 18, do not create an account. Contact us to remove any data submitted by a minor.</p>
          </Section>

          <Section title="9. Changes">
            <p>We will update the date at the top when this policy changes and highlight material changes in the Journal. Continued use after changes means you accept the updated policy.</p>
          </Section>
        </div>
      </div>
      <Footer />
    </>
  );

  if (user) return <AppShell>{body}</AppShell>;
  return <div className="relative min-h-screen text-white"><TopNav variant="app" />{body}</div>;
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-[16px] font-semibold tracking-tight text-white">{title}</h2>
      <div className="mt-2 text-[14.5px] leading-relaxed text-white/70">{children}</div>
    </section>
  );
}

function Card({ icon: Icon, title, kicker, text }) {
  return (
    <div className="glass rounded-2xl p-4 flex gap-3">
      <div className="w-9 h-9 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-white/70" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-white/40">{kicker}</div>
        <div className="text-[14px] font-semibold text-white">{title}</div>
        <div className="mt-1 text-[12.5px] text-white/60 leading-relaxed">{text}</div>
      </div>
    </div>
  );
}
